/**
 * Audio Device Utilities
 * Provides functions to capture and format audio device information for audit logging
 */

// Cache for device information to avoid repeated API calls
let deviceCache = null;
let deviceCacheTimestamp = 0;
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Get all available audio devices
 * @returns {Promise<Object>} Object containing audioInputs and audioOutputs arrays
 */
export const getAvailableDevices = async () => {
  const now = Date.now();
  
  // Return cached data if it's still fresh
  if (deviceCache && (now - deviceCacheTimestamp) < CACHE_DURATION) {
    return deviceCache;
  }
  
  // Check if we're in a secure context (HTTPS or localhost)
  if (typeof window !== 'undefined') {
    const isSecureContext = window.isSecureContext || 
                           window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
    
    if (!isSecureContext) {
      console.warn('Media device access requires HTTPS. Returning empty device list.');
      return { audioInputs: [], audioOutputs: [] };
    }
  }
  
  // Check if mediaDevices API is available
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.warn('getUserMedia is not supported in this browser');
    return { audioInputs: [], audioOutputs: [] };
  }
  
  try {
    await navigator.mediaDevices.getUserMedia({ audio: true });
    const devices = await navigator.mediaDevices.enumerateDevices();
    
    const audioOutputs = devices.filter(device => 
      device.kind === 'audiooutput' && 
      device.deviceId !== 'communications' && 
      device.deviceId !== 'default'
    );
    
    const audioInputs = devices.filter(device => device.kind === "audioinput");
    
    deviceCache = { audioInputs, audioOutputs };
    deviceCacheTimestamp = now;
    
    return deviceCache;
  } catch (err) {
    console.error("Error loading devices:", err);
    // If it's a secure context error, log a helpful message
    if (err.message && (err.message.includes('secure context') || err.message.includes('HTTPS'))) {
      console.warn('Media device access requires HTTPS connection');
    }
    return { audioInputs: [], audioOutputs: [] };
  }
};

/**
 * Get device name by device ID
 * @param {string} deviceId - The device ID to look up
 * @param {string} deviceType - Type of device ('input' or 'output')
 * @returns {Promise<string>} User-friendly device name
 */
export const getDeviceNameById = async (deviceId, deviceType) => {
  if (!deviceId || deviceId === "" || deviceId === "default") {
    return "Default System Device";
  }
  
  try {
    const devices = await getAvailableDevices();
    const deviceList = deviceType === 'input' ? devices.audioInputs : devices.audioOutputs;
    
    const device = deviceList.find(d => d.deviceId === deviceId);
    return device ? (device.label || "Unknown Device") : "Unknown Device";
  } catch (error) {
    console.warn('Failed to get device name:', error);
    return "Unknown Device";
  }
};

/**
 * Get current audio device selections from localStorage
 * @returns {Object} Object containing current audio device selections
 */
export const getCurrentAudioDeviceSelections = () => {
  const ringingDevice = localStorage.getItem("selectedConsultRingingDevice") || "";
  const callingDevice = localStorage.getItem("selectedConsultCallingDevice") || "";
  const micInput = localStorage.getItem("selectedConsultMicInput") || "";
  
  return {
    ringingDevice,
    callingDevice,
    micInput
  };
};

/**
 * Get device information for audit logging
 * @param {string} deviceId - The device ID from localStorage
 * @param {string} deviceType - Type of device (ringing, calling, mic)
 * @returns {Object} Formatted device information
 */
export const getDeviceInfo = (deviceId, deviceType) => {
  if (!deviceId || deviceId === "") {
    return {
      deviceType: deviceType,
      selectionType: "default",
      deviceId: "default",
      deviceName: "Default System Device"
    };
  }
  
  return {
    deviceType: deviceType,
    selectionType: "specific",
    deviceId: deviceId,
    deviceName: deviceId // In a real implementation, you might want to resolve this to a human-readable name
  };
};

/**
 * Get comprehensive audio device information for consultation events
 * @returns {Object} Complete audio device information for audit logging
 */
export const getConsultationAudioDeviceInfo = () => {
  const deviceSelections = getCurrentAudioDeviceSelections();
  
  return {
    ringingDevice: getDeviceInfo(deviceSelections.ringingDevice, "ringing"),
    callingDevice: getDeviceInfo(deviceSelections.callingDevice, "calling"),
    micInput: getDeviceInfo(deviceSelections.micInput, "microphone")
  };
};

/**
 * Format audio device information for display in audit reports
 * @param {Object} audioDeviceInfo - Audio device information object
 * @returns {string} Formatted string for display
 */
export const formatAudioDeviceInfoForDisplay = (audioDeviceInfo) => {
  if (!audioDeviceInfo) return "No audio device information available";
  
  const { ringingDevice, callingDevice, micInput } = audioDeviceInfo;
  
  let displayText = "Audio Devices: ";
  const devices = [];
  
  if (ringingDevice) {
    devices.push(`Ringing: ${ringingDevice.selectionType === 'default' ? 'Default' : ringingDevice.deviceId}`);
  }
  
  if (callingDevice) {
    devices.push(`Calling: ${callingDevice.selectionType === 'default' ? 'Default' : callingDevice.deviceId}`);
  }
  
  if (micInput) {
    devices.push(`Microphone: ${micInput.selectionType === 'default' ? 'Default' : micInput.deviceId}`);
  }
  
  return displayText + devices.join(", ");
};

/**
 * Check if audio device information should be included in audit log
 * @param {string} action - The audit action being logged
 * @returns {boolean} Whether to include audio device information
 */
export const shouldIncludeAudioDeviceInfo = (action) => {
  const consultationActions = [
    'Consultation',
    'consultation',
    'consult',
    'call',
    'video',
    'audio'
  ];
  
  return consultationActions.some(consultAction => 
    action.toLowerCase().includes(consultAction.toLowerCase())
  );
};



// WEBPACK FOOTER //
// ./src/utils/audioDeviceUtils.js